import { IAction, IOperation, IObjectSignature } from 'types';
import {
    operation,
    OperationsService
} from 'services/operations/operations-service';
import { select, all, call } from 'typed-redux-saga';
import { getOperation, getOmjsViewInstance, getOmjsLayerConfig } from 'selectors';
import { OmjsService } from 'services/omjs/omjs-service';
import { ObjectsHelper } from 'utils';
import { ComparableLayers } from 'project-constants';
import { routes } from 'routes';
import {
    Service,
    bindActionsTyped,
    latest
} from '../service-utils';

export interface IComparisonOperationValue {
    layer_structure: omjs.ILayerColumnJSON[];
    objects: omjs.IObjectJSON[];
    object_ids: IObjectSignature[];
}

export const getDefaultServiceState = (
    merge_data: Partial<IComparisonOperationValue> = {}
): IComparisonOperationValue => ({
    'layer_structure': [],
    'objects': [],
    'object_ids': [
        // { layer_code: 'plots_1', object_id: 5539 },
        // { layer_code: 'plots_2', object_id: 6191 },
        // { layer_code: 'plots_3', object_id: 5540 }
    ],
    ...merge_data
});

class ObjectsComparisonService extends Service {
    static serviceName = 'ObjectsComparisonService';
    static generatedStopActionTypes: string[] = [];
    static generatedWatchers: Function[] = [];

    @latest
    static* addItem(action: IAction<IObjectSignature>) {
        let comparison_operation: IOperation<IComparisonOperationValue> = yield* select(
            getOperation,
            'OBJECTS_COMPARISON'
        );
        let { object_ids } = comparison_operation.data;
        let { payload } = action;

        if (
            ComparableLayers.has(payload.layer_code)
            && !object_ids.find(i => (
                i.layer_code === payload.layer_code
                && i.object_id === payload.object_id
            ))
        ) {
            let new_ids = [
                ...object_ids,
                payload
            ];

            if (new_ids.length <= 3) {
                yield OperationsService.setData(
                    'OBJECTS_COMPARISON',
                    {
                        ...comparison_operation.data,
                        object_ids: new_ids
                    }
                );
            }
        }

        return null;
    }

    @latest
    static* removeItem(action: IAction<IObjectSignature>) {
        let comparison_operation: IOperation<IComparisonOperationValue> = yield* select(
            getOperation,
            'OBJECTS_COMPARISON'
        );
        let { object_ids } = comparison_operation.data;
        let { payload } = action;

        let existing_item = object_ids.find(i => (
            i.layer_code === payload.layer_code
            && i.object_id === payload.object_id
        ));
        if (
            ComparableLayers.has(payload.layer_code)
            && existing_item
        ) {
            let new_object_ids = [ ...object_ids ];
            new_object_ids.splice(new_object_ids.indexOf(existing_item), 1);

            if (new_object_ids.length) {
                yield OperationsService.setData(
                    'OBJECTS_COMPARISON',
                    {
                        ...comparison_operation.data,
                        object_ids: new_object_ids
                    }
                );
            } else {
                yield ObjectsComparisonService.clearLoadedData();
                yield OperationsService.setData(
                    'OBJECTS_COMPARISON',
                    getDefaultServiceState()
                );
            }
        }

        return null;
    }

    @latest
    @operation('OBJECTS_COMPARISON')
    static* loadDataForFullWindow() {
        let comparison_operation: IOperation<IComparisonOperationValue> = yield* select(
            getOperation,
            'OBJECTS_COMPARISON'
        );

        let { object_ids } = comparison_operation.data;

        /**
         * `layer_code` Нужен для работы со структурой и поиском конфига для слоя
         * Для этого проекта считаем что у любого слоя из `ComparableLayers` будет
         * одинаковая структура и одинаковый конфиг поэтому просто берем первый код слоя
         * и работаем и сним
         */
        /* eslint-disable-next-line */
        let layer_code = object_ids[0].layer_code;
        let layer_config = yield* select(getOmjsLayerConfig, layer_code);

        if (layer_config === false) {
            throw new Error(`Не нашли конфиг для слоя ${layer_code}`);
        }

        let load_objects_sagas = object_ids.map(
            item => call(OmjsService.loadProjectObject, item.layer_code, item.object_id, {
                raw: true,
                returnGeom: 0,
                fields: '*', // все поля паком
                f: 'fjson'
            })
        );

        // @ts-ignore
        let [
            structure,
            ...objects
        ]: [
            omjs.ILayerColumnJSON[],
            ...omjs.IObjectJSON[]
        ] = yield* all([
            call(OmjsService.loadProjectLayerStructure, layer_code),
            ...load_objects_sagas
        ]);

        return {
            ...comparison_operation.data,
            layer_structure: ObjectsHelper.filterStructure(structure, layer_config),
            objects
        } as IComparisonOperationValue;
    }

    /**
     * Функция очищает только данные которые грузились (структура, объкты).
     * Сами ID объектов для отображения в выпадающем меню она не очищает.
     * Эта функция вызывается при закрытии полного окна сравнения для очищения
     * загруженных объектов и структуры
     */
    @latest
    static* clearLoadedData() {
        let comparison_operation: IOperation<IComparisonOperationValue> = yield* select(
            getOperation,
            'OBJECTS_COMPARISON'
        );

        // Сначала запоминаем информацию которая уже была заполнена
        let { data } = comparison_operation;

        // Чистим операцию
        OperationsService.clearOperation({ type: '_', payload: 'OBJECTS_COMPARISON' });

        // Восстанавливаем часть информации в которой содержались данные об добавленных на сравнение объктов
        yield OperationsService.setData(
            'OBJECTS_COMPARISON',
            getDefaultServiceState({
                object_ids: data.object_ids
            })
        );
    }
}


export const objectsComparisonService: ObjectsComparisonService = new ObjectsComparisonService();
export const actions = bindActionsTyped(ObjectsComparisonService, objectsComparisonService);
